import { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Users } from "lucide-react";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const SCOPES = [
  ["current", "ACTUALES"],
  ["past", "PASADOS"],
  ["month", "MES"],
];

const datesText = (c, formatDate) =>
  [c.date1, c.days >= 2 && c.date2, c.days >= 3 && c.date3]
    .filter(Boolean)
    .map(formatDate)
    .join(" + ");

const hoursText = (c) =>
  [
    [c.time_start_day1, c.time_end_day1],
    [c.time_start_day2, c.time_end_day2],
    Number(c.days) >= 3 ? [c.time_start_day3, c.time_end_day3] : [null, null],
  ]
    .filter(([s, e]) => s && e)
    .map(([s, e]) => `${s}–${e}`)
    .join(" · ");

// Curso ya impartido: solo se consulta (sin abrir asistencias ni exámenes)
const PastCard = ({ c, formatDate }) => (
  <div
    className="rounded-md border border-zinc-200 bg-zinc-50 p-3"
    data-testid={`instructor-past-course-${c.id}`}
  >
    <div className="font-semibold text-zinc-700 text-sm">
      {c.client_name || c.name}
      <span className="ml-2 inline-block px-1.5 py-0.5 bg-zinc-200 text-zinc-700 text-[10px] font-bold rounded">
        IMPARTIDO
      </span>
    </div>
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-600">
      <span className="inline-flex items-center gap-1">
        <Calendar className="h-3 w-3 text-zinc-400" />
        {datesText(c, formatDate)}
      </span>
      {(c.city || c.address) && (
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3 w-3 text-zinc-400" />
          {c.city || c.address}
        </span>
      )}
      <span className="inline-flex items-center gap-1" data-testid={`instructor-past-course-students-${c.id}`}>
        <Users className="h-3 w-3 text-zinc-400" />
        {c.students_count ?? 0} {c.students_count === 1 ? "alumno" : "alumnos"}
      </span>
    </div>
  </div>
);

const CourseButton = ({ c, active, onSelect, formatDate }) => (
  <button
    onClick={() => onSelect(c.id)}
    className={`w-full text-left rounded-md border p-3 transition-colors ${
      active ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 bg-white hover:bg-zinc-50"
    }`}
    data-testid={`instructor-course-${c.id}`}
  >
    <div className="font-semibold text-zinc-950 text-sm">
      {c.client_name || c.name}
      {c.in_progress && (
        <span className="ml-2 inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded">
          EN CURSO
        </span>
      )}
      {c.is_today && !c.in_progress && (
        <span className="ml-2 inline-block px-1.5 py-0.5 bg-zinc-900 text-white text-[10px] font-bold rounded">
          HOY
        </span>
      )}
    </div>
    <div className="mt-1 text-xs text-zinc-600 inline-flex flex-wrap items-center gap-3">
      <span className="inline-flex items-center gap-1">
        <Calendar className="h-3 w-3 text-zinc-400" />
        {datesText(c, formatDate)}
      </span>
      {hoursText(c) && (
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3 text-zinc-400" />
          {hoursText(c)}
        </span>
      )}
    </div>
  </button>
);

const EMPTY = {
  current: "No tienes cursos asignados.",
  past: "Todavía no has impartido ningún curso.",
  month: "No tienes cursos este mes.",
};

export const InstructorCourseList = ({ courses, activeId, onSelect, formatDate }) => {
  const [scope, setScope] = useState("current");
  const [other, setOther] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (scope === "current") return;
    let cancelled = false;
    setLoading(true);
    adminApi
      .get("/instructor/courses", { params: { scope } })
      .then((r) => !cancelled && setOther((prev) => ({ ...prev, [scope]: r.data || [] })))
      .catch((e) => logError("No se pudieron cargar los cursos", e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const list = scope === "current" ? courses : other[scope] || [];
  return (
    <>
      <div className="mb-3 inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-1" data-testid="instructor-courses-scope">
        {SCOPES.map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setScope(value)}
            className={`h-8 px-3 rounded-sm text-xs font-bold tracking-wide transition-colors ${
              scope === value ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"
            }`}
            data-testid={`instructor-courses-scope-${value}`}
          >
            {label}
          </button>
        ))}
      </div>
      {loading && !other[scope] ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 text-sm text-zinc-500">
          Cargando…
        </div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 text-sm text-zinc-500" data-testid="instructor-courses-empty">
          {EMPTY[scope]}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="instructor-courses-list">
          {list.map((c) => (
            <li key={c.id}>
              {c.is_past ? (
                <PastCard c={c} formatDate={formatDate} />
              ) : (
                <CourseButton c={c} active={activeId === c.id} onSelect={onSelect} formatDate={formatDate} />
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
